import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Mail, Building2, Search } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { format, parseISO } from 'date-fns';

const PLAN_COLORS = {
  starter: 'bg-blue-100 text-blue-800',
  pro: 'bg-emerald-100 text-emerald-800',
  business: 'bg-violet-100 text-violet-800',
};

const fmt = (d) => {
  if (!d) return '—';
  try { return format(parseISO(d), 'PPp'); } catch { return d; }
};

const WaitlistView = () => {
  const { user, isPlatformAdmin } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!isPlatformAdmin) return;
    (async () => {
      try {
        const { data } = await api.get('/waitlist/entries');
        setEntries(data.entries || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [isPlatformAdmin]);

  // Hard client-side guard. Backend also enforces.
  if (user && !isPlatformAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const filtered = entries.filter((e) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      e.email?.toLowerCase().includes(q) ||
      e.name?.toLowerCase().includes(q) ||
      e.company?.toLowerCase().includes(q) ||
      e.plan?.toLowerCase().includes(q)
    );
  });

  const planCounts = entries.reduce((acc, e) => {
    const k = e.plan || 'unspecified';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="waitlist-view">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-manrope font-bold text-3xl text-slate-900 tracking-tight">Waitlist</h1>
          <p className="text-slate-500 mt-1">People who signed up for paid plans before billing went live.</p>
        </div>
        <Badge className="bg-slate-100 text-slate-700 text-sm px-3 py-1.5">
          {entries.length} {entries.length === 1 ? 'signup' : 'signups'}
        </Badge>
      </div>

      {/* Plan distribution */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {['starter', 'pro', 'business', 'unspecified'].map((plan) => (
          <Card key={plan} className="p-4">
            <p className="text-xs uppercase tracking-widest text-slate-400 font-medium">{plan}</p>
            <p className="font-manrope text-2xl font-bold text-slate-900 mt-1">
              {planCounts[plan] || 0}
            </p>
          </Card>
        ))}
      </div>

      <Card className="overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center gap-3">
          <Search className="h-4 w-4 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by email, name, company, or plan"
            className="border-0 shadow-none focus-visible:ring-0 px-0"
            data-testid="waitlist-search"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="spinner" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center">
            <Mail className="h-8 w-8 text-slate-300 mx-auto" />
            <p className="text-slate-500 mt-2 text-sm">
              {entries.length === 0
                ? 'No one on the waitlist yet. Share /pricing to get the first signup!'
                : 'No matches for your search.'}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Signed up</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((entry) => (
                <TableRow key={entry.id || entry.email} data-testid={`waitlist-row-${entry.email}`}>
                  <TableCell className="font-medium text-slate-900">{entry.email}</TableCell>
                  <TableCell className="text-slate-700">{entry.name || '—'}</TableCell>
                  <TableCell className="text-slate-700">
                    {entry.company ? (
                      <span className="inline-flex items-center gap-1.5">
                        <Building2 className="h-3 w-3 text-slate-400" />
                        {entry.company}
                      </span>
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    {entry.plan ? (
                      <Badge className={PLAN_COLORS[entry.plan] || 'bg-slate-100 text-slate-700'}>
                        {entry.plan}
                      </Badge>
                    ) : (
                      <span className="text-slate-400 text-sm">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-slate-500 text-xs uppercase tracking-wider">
                    {entry.source || 'unknown'}
                  </TableCell>
                  <TableCell className="text-slate-500 text-sm">{fmt(entry.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
};

export default WaitlistView;
